import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../baseQuery';
import { adminBase } from './adminBase';

export interface PeriodTemplate {
	id: string;
	school: string;
	name: string;
	periodNumber: number;
	startTime: string;
	endTime: string;
	daysOfWeek: number[];
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
}

export interface TimetableSlot {
	id: string;
	school: string;
	class: any; // populated { id, classNumber, section, academicYear, classCode }
	periodTemplate: any; // populated { id, name, periodNumber, startTime, endTime }
	dayOfWeek: number;
	weekStartDate: string;
	dayBucket: string;
	startsAt: string;
	endsAt: string;
	trainers: any[]; // populated trainer minis
	isLabSlot: boolean;
	status: string;
	publishedAt: string | null;
	publishedBy: string | null;
	createdAt: string;
	updatedAt: string;
}

export const masterAdminTimetableApi = createApi({
	reducerPath: 'masterAdminTimetableApi',
	baseQuery: axiosBaseQuery,
	tagTypes: ['PeriodTemplates', 'TimetableSlots', 'SlotAudit', 'TrainerRequests'],
	endpoints: (builder) => ({
		listPeriodTemplates: builder.query<
			{ templates: PeriodTemplate[] },
			{ schoolId: string }
		>({
			query: ({ schoolId }) => ({
				url: `${adminBase()}/timetable/period-templates`,
				params: { schoolId },
			}),
			providesTags: ['PeriodTemplates'],
		}),
		createPeriodTemplate: builder.mutation<
			PeriodTemplate,
			{
				schoolId: string;
				name: string;
				periodNumber: number;
				startTime: string;
				endTime: string;
				daysOfWeek: number[];
			}
		>({
			query: (body) => ({
				url: `${adminBase()}/timetable/period-templates`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['PeriodTemplates'],
		}),
		updatePeriodTemplate: builder.mutation<
			PeriodTemplate,
			{ id: string; updates: Partial<PeriodTemplate> }
		>({
			query: ({ id, updates }) => ({
				url: `${adminBase()}/timetable/period-templates/${id}`,
				method: 'PUT',
				data: updates,
			}),
			invalidatesTags: ['PeriodTemplates'],
		}),
		deletePeriodTemplate: builder.mutation<{ message: string }, string>({
			query: (id) => ({
				url: `${adminBase()}/timetable/period-templates/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['PeriodTemplates'],
		}),

		listSlots: builder.query<
			{
				weekStartDate?: string;
				startDate?: string;
				endDate?: string;
				timezone: string;
				slots: TimetableSlot[];
			},
			{
				schoolId: string;
				weekStartDate?: string;
				startDate?: string;
				endDate?: string;
			}
		>({
			query: ({ schoolId, weekStartDate, startDate, endDate }) => ({
				url: `${adminBase()}/timetable/slots`,
				params: { schoolId, weekStartDate, startDate, endDate },
			}),
			providesTags: ['TimetableSlots'],
		}),
		bulkCreateSlots: builder.mutation<
			{ created: number; skipped: number; weekStartDate: string },
			{
				schoolId: string;
				weekStartDate: string;
				classIds: string[];
				templateIds: string[];
			}
		>({
			query: (body) => ({
				url: `${adminBase()}/timetable/slots/bulk`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['TimetableSlots'],
		}),
		addSingleSlot: builder.mutation<
			TimetableSlot,
			{
				schoolId: string;
				classId: string;
				dayBucket: string;
				startTime: string;
				endTime: string;
				isLabSlot?: boolean;
			}
		>({
			query: (body) => ({
				url: `${adminBase()}/timetable/slots/single`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['TimetableSlots'],
		}),
		updateSlot: builder.mutation<
			TimetableSlot,
			{ id: string; updates: { isLabSlot?: boolean } }
		>({
			query: ({ id, updates }) => ({
				url: `${adminBase()}/timetable/slots/${id}`,
				method: 'PATCH',
				data: updates,
			}),
			invalidatesTags: ['TimetableSlots'],
		}),
		deleteSlot: builder.mutation<
			{ message: string; deletedCount: number; scope: 'single' | 'series' },
			{ id: string; scope?: 'single' | 'series' } | string
		>({
			query: (arg) => {
				const id = typeof arg === 'string' ? arg : arg.id;
				const scope =
					typeof arg === 'string' ? 'single' : arg.scope || 'single';
				return {
					url: `${adminBase()}/timetable/slots/${id}`,
					method: 'DELETE',
					params: { scope },
				};
			},
			invalidatesTags: ['TimetableSlots'],
		}),
		publishWeek: builder.mutation<
			{ published: number; weekStartDate: string },
			{ schoolId: string; weekStartDate: string }
		>({
			query: (body) => ({
				url: `${adminBase()}/timetable/slots/publish`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['TimetableSlots'],
		}),
		wizardGenerateSlots: builder.mutation<
			{ created: number; skipped: number; totalDays: number },
			{
				schoolId: string;
				classIds: string[];
				cadence: '1x' | '2x' | '3x' | 'daily' | 'custom';
				customDays?: number[];
				startTime: string;
				endTime: string;
				startDate: string;
				endDate: string;
				isLabSlot: boolean;
				combineClasses?: boolean;
			}
		>({
			query: (body) => ({
				url: `${adminBase()}/timetable/slots/wizard-generate`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['TimetableSlots'],
		}),
		wizardPreviewSlots: builder.mutation<
			{
				totalDays: number;
				wouldCreate: number;
				duplicates: number;
				perClass: {
					classId: string;
					classLabel: string;
					wouldCreate: number;
					duplicates: number;
					duplicateDates: string[];
				}[];
			},
			{
				schoolId: string;
				classIds: string[];
				cadence: '1x' | '2x' | '3x' | 'daily' | 'custom';
				customDays?: number[];
				startTime: string;
				endTime: string;
				startDate: string;
				endDate: string;
			}
		>({
			query: (body) => ({
				url: `${adminBase()}/timetable/slots/wizard-preview`,
				method: 'POST',
				data: body,
			}),
		}),
		getSlotAudit: builder.query<{ logs: any[] }, string>({
			query: (id) => ({
				url: `${adminBase()}/timetable/slots/${id}/audit`,
			}),
			providesTags: (_r, _e, id) => [{ type: 'SlotAudit', id }],
		}),
		listTrainerRequests: builder.query<
			{ requests: any[]; pendingCount: number },
			{ status?: string; schoolId?: string } | void
		>({
			query: (arg) => ({
				url: `${adminBase()}/timetable/requests`,
				params: arg || undefined,
			}),
			providesTags: ['TrainerRequests'],
		}),
		decideTrainerRequest: builder.mutation<
			any,
			{ id: string; decision: 'approved' | 'rejected'; note?: string }
		>({
			query: ({ id, ...body }) => ({
				url: `${adminBase()}/timetable/requests/${id}/decide`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['TrainerRequests', 'TimetableSlots'],
		}),
	}),
});

export const {
	useListPeriodTemplatesQuery,
	useCreatePeriodTemplateMutation,
	useUpdatePeriodTemplateMutation,
	useDeletePeriodTemplateMutation,
	useListSlotsQuery,
	useBulkCreateSlotsMutation,
	useAddSingleSlotMutation,
	useUpdateSlotMutation,
	useDeleteSlotMutation,
	usePublishWeekMutation,
	useWizardGenerateSlotsMutation,
	useWizardPreviewSlotsMutation,
	useGetSlotAuditQuery,
	useListTrainerRequestsQuery,
	useDecideTrainerRequestMutation,
} = masterAdminTimetableApi;
